import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext, type EntityInteractionContext, type PointerDownGuardResult, type DoubleClickResult } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { ALL_CONNECTOR_SIDES, getConnectorPointForRect } from '../../entityTypes';
import { CanvasTextBlock } from './TextBlockNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Edit2, ToggleLeft, ToggleRight, Layers, Trash2, Square } from 'lucide-react';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { buildGroupSubmenu, buildMultiDeleteItem, buildColorGridSubmenu, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const BG_COLOR_OPTIONS = [
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#f3f4f6' },
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: '#dbeafe' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#dcfce7' },
  { id: 'yellow', label: t('canvasEntity.colorYellow'), color: '#fef9c3' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#fee2e2' },
  { id: 'purple', label: t('canvasEntity.colorPurple'), color: '#ede9fe' },
];

const TEXT_COLOR_OPTIONS = [
  { id: 'dark', label: t('canvasEntity.colorDark'), color: '#1f2937' },
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#6b7280' },
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: '#2563eb' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#16a34a' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#dc2626' },
  { id: 'white', label: t('canvasEntity.colorWhite'), color: '#ffffff' },
];

export class TextBlockHandler extends EntityKindHandler {
  readonly kind = 'textblock';
  readonly Component = CanvasTextBlock;
  readonly dataAttribute = 'data-canvas-textblock';
  canConnect = true;
  canResize = true;
  canInlineEdit = true;

  getAddPickerEntry(): AddPickerEntry {
    return { label: 'TextBlock', icon: Square, iconColor: '#f59e0b', priority: 30, mode: 'instant' };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    if (!entity.connectable) return [];
    return ALL_CONNECTOR_SIDES.map(side => ({
      side,
      ...getConnectorPointForRect(entity.x, entity.y, entity.width, entity.height, side),
    }));
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const data = entity.data as any;
    return [
      {
        name: 'backgroundColor',
        label: t('canvasEntity.background'),
        type: 'color',
        options: BG_COLOR_OPTIONS,
        getValue: () => data.backgroundColor,
        apply: (v) => store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { backgroundColor: v as string } }),
      },
      {
        name: 'textColor',
        label: t('canvasEntity.textColor'),
        type: 'color',
        options: TEXT_COLOR_OPTIONS,
        getValue: () => data.textColor,
        apply: (v) => store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { textColor: v as string } }),
      },
      {
        name: 'borderColor',
        label: t('canvasEntity.borderColor'),
        type: 'color',
        options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
        getValue: () => data.borderColor ?? '',
        apply: (v) => {
          const color = v as string;
          if (!color) {
            store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { borderColor: color } });
          }
        },
      },
      {
        name: 'borderWidth',
        label: t('canvasEntity.borderWidth'),
        type: 'select',
        options: BORDER_WIDTH_OPTIONS,
        getValue: () => data.borderColor ? String(data.borderWidth ?? 1) : 'none',
        apply: (v) => {
          if (v === 'none') {
            store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'textblock.updateData', entityId: entity.id, dataChanges: { borderWidth: Number(v) } });
          }
        },
      },
      {
        name: 'connectable',
        label: 'Connectors',
        type: 'toggle',
        getValue: () => entity.connectable,
        apply: (v) => store.send({ type: 'entity.setConnectable', entityId: entity.id, connectable: !!v }),
      },
    ];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers } = ctx;
    const textBlockId = entity.id;
    const data = entity.data as any;
    const items: ContextMenuItem[] = [];

    // --- Pos. 1: Edit text ---
    items.push({
      id: 'edit-text',
      label: t('canvasEntity.editText'),
      icon: Edit2,
      onClick: () => {
        canvasSelectionStore.send({ type: 'entity.selectOne', entityId: textBlockId });
        canvasSelectionStore.send({ type: 'editing.start', elementId: textBlockId, kind: 'textblock' });
      },
    });

    // --- Pos. 2: Background color submenu ---
    items.push(buildColorGridSubmenu({
      id: 'bg-color-submenu',
      label: t('canvasEntity.backgroundColor'),
      separator: true,
      currentColor: data.backgroundColor,
      handlerColors: [...BG_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'textblock.updateData', entityId: textBlockId, dataChanges: { backgroundColor: color } }),
    }));

    // --- Pos. 2: Text color submenu ---
    items.push(buildColorGridSubmenu({
      id: 'text-color-submenu',
      label: t('canvasEntity.textColor'),
      currentColor: data.textColor,
      handlerColors: [...TEXT_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'textblock.updateData', entityId: textBlockId, dataChanges: { textColor: color } }),
    }));

    // --- Border color submenu ---
    items.push(buildBorderColorSubmenu({
      id: 'border-color-submenu',
      label: t('canvasEntity.borderColor'),
      currentColor: data.borderColor,
      onSelectColor: (color) => store.send({ type: 'textblock.updateData', entityId: textBlockId, dataChanges: { borderColor: color } }),
      onRemoveBorder: () => store.send({ type: 'textblock.updateData', entityId: textBlockId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
    }));

    // --- Pos. 3: Toggle connectors ---
    items.push({
      id: 'toggle-connectors',
      label: entity.connectable ? t('canvasEntity.connectorsOff') : t('canvasEntity.connectorsOn'),
      icon: entity.connectable ? ToggleRight : ToggleLeft,
      onClick: () => store.send({ type: 'entity.setConnectable', entityId: textBlockId, connectable: !entity.connectable }),
    });

    // --- Pos. 4: Gruppe submenu ---
    items.push({ ...buildGroupSubmenu(ctx), separator: true });

    // --- Pos. 5: Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'entity.setLayer', entityId: textBlockId, layerId });
      }),
    });

    // --- Pos. 7: Remove ---
    items.push({
      id: 'remove',
      label: t('canvasEntity.remove'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: textBlockId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    // --- Pos. 8: Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.elements'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

  onPointerDownGuard(ctx: EntityInteractionContext, _e: React.PointerEvent): PointerDownGuardResult | null {
    if (ctx.isEditing) return { handled: true };
    return null;
  }

  onDoubleClick(ctx: EntityInteractionContext, _e: React.MouseEvent): DoubleClickResult | null {
    if (ctx.redirectGroupId) return null; // let default group-enter happen
    if (ctx.isLocked) return { handled: true };
    canvasSelectionStore.send({ type: 'editing.start', elementId: ctx.entity.id, kind: 'textblock' });
    return { handled: true };
  }

}
